<script>
import bem from '@/utils/bem';
import CreateNewUser from '@/components/modals/Create-new-user.vue';
import {
  EllipsisOutlined,
  EditOutlined,
  EnvironmentOutlined,
  KeyOutlined,
  LockOutlined,
  DeleteOutlined,
} from '@ant-design/icons-vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import constants from '@/constants';
import createDebounce from '@/utils/debounce';
import pageUrls from '@/router/pageUrls';
import dateFormat from '@/utils/dateFormat';
import columns from './columns';

const { usersPage } = pageUrls;
const { roles } = constants;

const ROLE_COLOR_MAP = {
  1: 'blue',
  2: 'red',
  3: 'geekblue',
  4: 'cyan',
  5: 'purple',
  6: 'purple',
  7: 'cyan',
  8: 'cyan',
  9: 'geekblue',
  10: 'default',
  11: 'lime',
  12: 'orange',
  13: 'green',
};

const b = bem('users-page');

export default {
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      limit: 10,
      offset: 0,
      searchValue: '',
      debounce: createDebounce(),
      usersPage,
      // Track which row has the password modal open
      passwordModalUserId: null,
      // Inline editing for role/company: { userId, field }
      editingCell: null,
    };
  },
  components: {
    EllipsisOutlined,
    EditOutlined,
    EnvironmentOutlined,
    KeyOutlined,
    LockOutlined,
    DeleteOutlined,
    CreateNewUser,
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getUsersAction: 'users/getUsers',
      updateUserAction: 'users/updateUser',
      changePasswordAction: 'users/changePassword',
      resetPasswordAction: 'users/resetPassword',
      deleteUserAction: 'users/deleteUser',
      searchUsers: 'users/searchUsers',
      getCompaniesAction: 'companies/getCompanies',
    }),
    setPagination(page, pageSize) {
      this.offset = (page - 1) * pageSize;
      this.limit = pageSize;
    },
    getUsers() {
      this.getUsersAction({
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
      });
    },
    getCompanyName(record) {
      if (!record.companyId) return 'FS';
      if (record.company?.displayName) return record.company.displayName;
      const found = (this.companies || [])
        .find((c) => c.id === record.companyId);
      return found ? found.displayName : 'FS';
    },
    getRoleName(roleId) {
      const role = roles.find((r) => r.value === roleId);
      return role ? role.label : roleId;
    },
    getRoleColor(roleId) {
      return ROLE_COLOR_MAP[roleId] || 'default';
    },
    formatDate(date) {
      if (!date) return '—';
      return dateFormat(date, 'DD.MM.YYYY');
    },
    startEditing(userId, field) {
      this.editingCell = { userId, field };
    },
    isEditing(userId, field) {
      return this.editingCell?.userId === userId && this.editingCell?.field === field;
    },
    saveField(record, field, value) {
      this.updateUserAction({
        user: {
          id: record.id,
          firstName: record.firstName,
          lastName: record.lastName,
          role: field === 'role' ? value : record.role,
          companyId: field === 'companyId' ? value : record.companyId,
          isDeleted: record.isDeleted,
        },
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
      });
      this.editingCell = null;
    },
    resetPassword(email) {
      this.resetPasswordAction({ email });
    },
    deleteUser(id) {
      this.deleteUserAction({
        id,
        limit: this.limit,
        offset: this.offset,
        companyId: this.companyId,
      });
    },
    onSearch(e) {
      const { target: { value } } = e;
      this.searchValue = value;
      if (value) {
        this.debounce(() => {
          this.searchUsers({
            limit: this.limit,
            offset: this.offset,
            searchField: value.trim().toLowerCase(),
          });
        });
      }
    },
    onReset() {
      this.searchValue = '';
      this.getUsers();
    },
  },
  mounted() {
    this.getUsers();
    if (!this.isCompanyUser) {
      this.getCompaniesAction({ limit: 1000, offset: 0 });
    }
  },
  computed: {
    ...mapState({
      users: (state) => state.users.users,
      total: (state) => state.users.total,
      companies: (state) => state.companies.companies,
    }),
    ...mapGetters({
      isCompanyUser: 'auth/isCompanyUser',
    }),
    availableRoles() {
      if (this.companyId) {
        return roles.filter((r) => [12, 13].includes(r.value));
      }
      return roles;
    },
    companiesOptions() {
      return [
        { value: null, label: 'FS' },
        ...(this.companies || []).map((c) => ({
          value: c.id,
          label: c.displayName,
        })),
      ];
    },
    tableColumns() {
      const cols = columns();
      if (this.companyId) {
        return cols.filter((c) => c.key !== 'companyId');
      }
      return cols;
    },
  },
  watch: {
    offset() { this.getUsers(); },
    limit() { this.getUsers(); },
    companyId() { this.getUsers(); },
  },
};
</script>

<template>
  <div :class="b()">
    <!-- Top bar: search + create -->
    <div :class="b('toolbar')">
      <a-input-search
        v-model:value="searchValue"
        :class="b('search')"
        placeholder="Search users..."
        @change="onSearch"
      >
        <template #enterButton>
          <a-button @click="onReset">Reset</a-button>
        </template>
      </a-input-search>
      <CreateNewUser v-if="!companyId" @user-created="getUsers" />
    </div>

    <!-- Users table — read-only, compact -->
    <a-table
      :data-source="users"
      :columns="tableColumns"
      :pagination="{
        pageSize: limit,
        total,
        onChange: setPagination,
        showSizeChanger: true,
        showTotal: (t) => `${t} users`,
      }"
      size="middle"
      row-key="id"
      :class="b('table')"
    >
      <template #bodyCell="{ column, record }">
        <!-- User: name + email stacked -->
        <template v-if="column.key === 'user'">
          <div :class="b('user-cell')">
            <span :class="b('user-name')">
              {{ record.firstName }} {{ record.lastName }}
            </span>
            <span :class="b('user-email')">{{ record.email }}</span>
          </div>
        </template>

        <!-- Role: click tag to edit inline -->
        <template v-else-if="column.key === 'role'">
          <a-select
            v-if="isEditing(record.id, 'role')"
            :value="record.role"
            :options="availableRoles"
            size="small"
            style="width: 140px;"
            @change="(val) => saveField(record, 'role', val)"
            @blur="editingCell = null"
            :open="true"
          />
          <a-tag
            v-else
            :color="getRoleColor(record.role)"
            style="cursor: pointer;"
            @click="startEditing(record.id, 'role')"
          >
            {{ getRoleName(record.role) }}
          </a-tag>
        </template>

        <!-- Company: click to edit inline -->
        <template v-else-if="column.key === 'companyId'">
          <a-select
            v-if="isEditing(record.id, 'companyId')"
            :value="record.companyId"
            :options="companiesOptions"
            size="small"
            style="width: 180px;"
            @change="(val) => saveField(record, 'companyId', val)"
            @blur="editingCell = null"
            :open="true"
          />
          <span
            v-else
            role="button"
            tabindex="0"
            style="cursor: pointer;"
            @click="startEditing(record.id, 'companyId')"
            @keydown.enter="startEditing(record.id, 'companyId')"
          >
            {{ getCompanyName(record) }}
          </span>
        </template>

        <!-- Registered date -->
        <template v-else-if="column.key === 'addedAt'">
          {{ formatDate(record.addedAt) }}
        </template>

        <!-- Status badge -->
        <template v-else-if="column.key === 'isDeleted'">
          <a-badge
            :status="record.isDeleted ? 'error' : 'success'"
            :text="record.isDeleted ? 'Deleted' : 'Active'"
          />
        </template>

        <!-- Actions dropdown -->
        <template v-else-if="column.key === 'actions'">
          <a-dropdown :trigger="['click']">
            <a-button type="text" size="small">
              <EllipsisOutlined style="font-size: 18px;" />
            </a-button>
            <template #overlay>
              <a-menu>
                <a-menu-item
                  key="edit"
                  @click="$router.push(`${usersPage}/${record.id}`)"
                >
                  <EditOutlined /> Edit user
                </a-menu-item>
                <a-menu-item
                  key="addresses"
                  @click="$router.push(`${usersPage}/${record.id}`)"
                >
                  <EnvironmentOutlined /> Edit addresses
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item
                  key="reset-pw"
                  @click="resetPassword(record.email)"
                >
                  <KeyOutlined /> Reset password
                </a-menu-item>
                <a-menu-item
                  key="change-pw"
                  @click="passwordModalUserId = record.id"
                >
                  <LockOutlined /> Change password
                </a-menu-item>
                <a-menu-divider />
                <a-popconfirm
                  title="Sure to delete this user?"
                  @confirm="deleteUser(record.id)"
                >
                  <a-menu-item key="delete" danger>
                    <DeleteOutlined /> Delete
                  </a-menu-item>
                </a-popconfirm>
              </a-menu>
            </template>
          </a-dropdown>
        </template>
      </template>
    </a-table>

    <!-- Change password modal — open directly, skip the component's internal button -->
    <a-modal
      :open="!!passwordModalUserId"
      :footer="null"
      @cancel="passwordModalUserId = null"
    >
      <FormKit
        type="form"
        submit-label="Save"
        @submit="(values) => {
          changePasswordAction({
            user: { password: values.password, userId: passwordModalUserId },
          });
          passwordModalUserId = null;
        }"
      >
        <h3 style="margin-bottom: 12px;">Change account password</h3>
        <FormKit
          type="text"
          name="password"
          label="Password"
          validation="required|length:6|matches:/[^a-zA-Z]/"
          :validation-messages="{ matches: 'Please include at least one symbol' }"
          placeholder="Your password"
        />
        <FormKit
          type="text"
          name="password_confirm"
          label="Confirm password"
          placeholder="Confirm password"
          validation="required|confirm"
        />
      </FormKit>
    </a-modal>
  </div>
</template>

<style src="./style.scss" scoped lang="scss"></style>
